// passiveAssetRssi.js
// Convert a beacon RSSI reading into an estimated distance from the detecting
// reader, using the log-distance path-loss model:
//
//   distance_m = 10 ^ ((measuredPower - rssi_dBm) / (10 * n))
//
// where measuredPower is the calibrated RSSI at 1 m from the beacon and n is
// the environmental path-loss exponent (2 = free space, ~2.5-3 indoor, ~3.5-4
// cluttered warehouse).
//
// Many readers report RSSI as a positive decimal even though the real value
// is negative dBm. We default to interpreting positive values as -value (e.g.
// a payload of 17 means -17 dBm). Set `signedAsNegative` to false if your
// reader already supplies signed dBm.

export const DEFAULT_RSSI_OPTIONS = {
  measuredPower: -59,   // BLE convention; many beacons publish their own value
  environmentN: 2.5,    // typical indoor
  signedAsNegative: true,
  minRadiusMeters: 0.5,
  maxRadiusMeters: 50
};

export function normalizeRssiToDbm(rssi, signedAsNegative = true) {
  if (rssi === null || rssi === undefined || rssi === '') return null;
  const n = Number(rssi);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return n;
  return signedAsNegative ? -n : n;
}

export function rssiToDistance(rssi, options = {}) {
  const opts = { ...DEFAULT_RSSI_OPTIONS, ...options };
  const dbm = normalizeRssiToDbm(rssi, opts.signedAsNegative);
  if (dbm === null) return null;

  const exponent = (opts.measuredPower - dbm) / (10 * Math.max(0.1, opts.environmentN));
  const raw = Math.pow(10, exponent);

  if (!Number.isFinite(raw) || raw <= 0) return opts.maxRadiusMeters;
  return Math.max(opts.minRadiusMeters, Math.min(opts.maxRadiusMeters, raw));
}

// Bucket the estimated distance into a confidence band so the UI can color
// circles consistently (closer = higher confidence the beacon is near the
// reader).
export function rssiConfidenceBand(distance) {
  if (distance === null || distance === undefined) return 'unknown';
  if (distance <= 2) return 'immediate';   // basically at the reader
  if (distance <= 8) return 'near';        // same room / bay
  if (distance <= 25) return 'far';        // somewhere in the area
  return 'very-far';                       // weak signal, low confidence
}

export const CONFIDENCE_COLORS = {
  immediate: '#52c41a',
  near:      '#1890ff',
  far:       '#faad14',
  'very-far': '#ff4d4f',
  unknown:   '#bfbfbf'
};
